import { RefreshCw } from "lucide-react";
import { Checkbox } from "@/pages/interface/components/ui/checkbox";
import { Button } from "@/pages/interface/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/pages/interface/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { StatusChip } from "./StatusChip";
import type { SyncStoryRow } from "@/api/syncApi";
import type { RowUiState } from "./useSyncEngine";
import type { VendorConfig } from "./vendorConfig";

export interface SyncRowProps {
    row: SyncStoryRow;
    selected: boolean;
    highlighted?: boolean;
    uiState?: RowUiState;
    vendor: VendorConfig;
    actionsDisabled?: boolean;
    onToggle: () => void;
    onAction: () => void;
    onReview: () => void;
    onLinkOut?: () => void;
}

const ACTION_LABEL: Partial<Record<SyncStoryRow["status"], string>> = {
    NOT_SYNCED: "Sync",
    LOCAL_CHANGED: "Re-sync",
    REMOTE_CHANGED: "Update",
    CONFLICT: "Resolve",
    ORPHANED: "Push as new",
};
const REVIEW_STATUSES = new Set(["REMOTE_CHANGED", "CONFLICT"]);

function formatRelative(iso: string | null): string {
    if (!iso) return "Never synced";
    const diffMs = Date.now() - new Date(iso).getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "Just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 30) return `${day}d ago`;
    return new Date(iso).toLocaleDateString();
}

export function SyncRow({ row, selected, highlighted, uiState, vendor, actionsDisabled, onToggle, onAction, onReview, onLinkOut }: SyncRowProps) {
    const inFlight = !!uiState?.inFlight;
    const needsReview = REVIEW_STATUSES.has(row.status);
    const actionLabel = ACTION_LABEL[row.status];
    const handleAction = needsReview ? onReview : onAction;
    const showRetry = !!row.last_error && !needsReview;

    return (
        <div
            className={cn(
                "flex items-center gap-3 border-b border-gray-100 px-3 py-2.5 last:border-b-0",
                selected && "bg-[#f7f9ff]/70",
                highlighted && "border-l-2 border-l-[#94b2ff] bg-[#f7f9ff]/40 pl-2.5"
            )}
        >
            <Checkbox
                aria-label={row.title}
                checked={selected}
                disabled={row.status === "NOT_READY" || actionsDisabled}
                onCheckedChange={onToggle}
                className="data-[state=checked]:bg-[#1954ed] data-[state=checked]:border-[#1954ed]"
            />
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">{row.title}</p>
                <p className="truncate text-xs text-gray-500">{row.subtitle}</p>
                {row.last_error && (
                    <p className="mt-0.5 truncate text-xs text-rose-600" title={row.last_error}>
                        {row.last_error}
                    </p>
                )}
            </div>
            {/* Fixed-width status column: uniform pill with the relative time
                stacked underneath, so pills and times line up across rows.
                216px per the 1B handoff — the synced chip's link glyph needs
                the extra room. SyncPanel now also renders rows while the
                provider connection is broken (see actionsDisabled), so a
                synced row's external_url still links out — opening an
                already-created remote ticket needs no OAuth of our own. */}
            <div className="flex w-[216px] shrink-0 flex-col items-center gap-1">
                <StatusChip
                    status={row.status}
                    externalKey={row.external_key}
                    href={row.status === "SYNCED" && row.external_url ? row.external_url : undefined}
                    vendorLabel={vendor.label}
                    inFlight={inFlight}
                    step={uiState?.step}
                    className="w-full"
                    onLinkOut={onLinkOut}
                />
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="text-[11.5px] leading-tight text-gray-400">
                            {formatRelative(row.last_synced_at)}
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        {row.last_synced_at ? new Date(row.last_synced_at).toLocaleString() : "Never synced"}
                    </TooltipContent>
                </Tooltip>
            </div>
            {/* Fixed-width action column: uniform buttons; empty spacer keeps
                rows without an action aligned with the rest. */}
            <div className="w-32 shrink-0">
                {showRetry ? (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={inFlight || actionsDisabled}
                        onClick={onAction}
                        className="w-full border-[#ecf1ff] text-[#103699] hover:bg-[#f7f9ff]"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Retry
                    </Button>
                ) : actionLabel ? (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={inFlight || actionsDisabled}
                        onClick={handleAction}
                        title={`${actionLabel} in ${vendor.label}`}
                        className="w-full border-[#ecf1ff] text-[#103699] hover:bg-[#f7f9ff]"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> {actionLabel}
                    </Button>
                ) : null}
            </div>
        </div>
    );
}
